import { defineStore } from 'pinia'
import api from '@/services/api'

export const usePersonnelStore = defineStore('personnel', {
  state: () => ({
    personnel: {
      data: [],
      total: 0,
      countByGender: { M: 0, F: 0 },
      staffByDirection: []
    },
    filteredPersonnel: [],
    selectedAgent: null,
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      totalItems: 0,
      totalPages: 0
    },
    searchQuery: '',
    selectedDirection: 'Toutes'
  }),

  getters: {
    getPersonnelByDirection: (state) => (direction) => {
      // Accéder au tableau des agents via state.personnel.data
      const personnelData = state.personnel.data || []
      if (direction === 'Toutes') return personnelData
      return personnelData.filter(agent => agent.direction === direction)
    },
    
    getAgentById: (state) => (id) => {
      const personnelData = state.personnel.data || []
      return personnelData.find(agent => agent.id === parseInt(id) || agent.id === id)
    },
    
    getDirections: (state) => {
      const personnelData = state.personnel.data || []
      const directions = [...new Set(personnelData.map(agent => agent.direction))]
      return ['Toutes', ...directions]
    },
    
    getStatsBySexe: (state) => {
      // Utiliser directement la clé countByGender ajoutée par le backend
      const countByGender = state.personnel.countByGender || { M: 0, F: 0 }
      
      return [
        { sexe: 'M', total: countByGender.M || 0 },
        { sexe: 'F', total: countByGender.F || 0 }
      ]
    },
    
    getStatsByDirection: (state) => {
      // Utiliser la structure de donnees correcte
      const personnelData = state.personnel.data || []
      const stats = {}
      
      personnelData.forEach(agent => {
        const dir = agent.direction || agent.departement || 'Non défini'
        stats[dir] = (stats[dir] || 0) + 1
      })
      
      return stats
    },
    
    paginatedPersonnel: (state) => {
      return state.filteredPersonnel.slice(
        (state.pagination.page - 1) * state.pagination.limit,
        state.pagination.page * state.pagination.limit
      )
    }
  },

  actions: {
    async fetchPersonnel() {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.getPersonnelList()
        // Structure attendue de la réponse API
        if (response && response.data) {
          // Mettre à jour la structure complète avec toutes les propriétés
          this.personnel = {
            data: response.data.data || [],
            total: response.data.total || 0,
            countByGender: response.data.countByGender || { M: 0, F: 0 },
            staffByDirection: response.data.staffByDirection || []
          }
          //console.log('Personnel data loaded:', this.personnel);
          
          // Initialiser explicitement filteredPersonnel avec les données
          this.filteredPersonnel = [...this.personnel.data];
          //console.log('Filtered personnel initialized with', this.filteredPersonnel.length, 'items');
          
          // Mettre à jour la pagination
          this.pagination.totalItems = this.filteredPersonnel.length;
          this.pagination.totalPages = Math.ceil(this.filteredPersonnel.length / this.pagination.limit);
          //console.log('Pagination updated:', this.pagination);
        }
      } catch (error) {
        this.error = error.message || 'Erreur lors du chargement du personnel'
        console.error('Erreur fetchPersonnel:', error)
      } finally {
        this.loading = false
      }
    },
    
    async fetchAgentById(id) {
      this.loading = true
      this.error = null
      
      try {
        const response = await api.getAgentById(id)
        this.selectedAgent = response.data
        return this.selectedAgent
      } catch (error) {
        this.error = error.message || `Erreur lors du chargement de l'agent ${id}`
        console.error('Erreur fetchAgentById:', error)
        return null
      } finally {
        this.loading = false
      }
    },
    
    setPage(page) {
      this.pagination.page = page
    },
    
    setSearchQuery(query) {
      this.searchQuery = query
      this.pagination.page = 1  // Retour à la première page après recherche
      this.applyFilters()
    },
    
    setSelectedDirection(direction) {
      this.selectedDirection = direction
      this.pagination.page = 1  // Retour à la première page après changement de filtre
      this.applyFilters()
    },
    
    applyFilters() {
      // S'assurer que nous travaillons avec le tableau des agents depuis this.personnel.data
      const personnelData = this.personnel.data || [] 

      let result = [...personnelData]
      
      //console.log('Selected direction:', this.selectedDirection);
      // Filtre par direction
      if (this.selectedDirection !== 'Toutes') { 
        
        // Recherche plus flexible pour la direction
        result = result.filter(agent => {
          // Vérifier tous les champs possibles qui pourraient contenir la direction
          const directionMatch = (agent.direction_id == this.selectedDirection) ; 
          return directionMatch;
        });
         
      }
      
      // Filtre par recherche
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(agent => {
          return (
            (agent.nom && agent.nom.toLowerCase().includes(query)) ||
            (agent.prenom && agent.prenom.toLowerCase().includes(query)) ||
            (agent.id && agent.id.toString().toLowerCase().includes(query)) ||
            (agent.departement && agent.departement.toLowerCase().includes(query)) ||
            (agent.direction && agent.direction.toLowerCase().includes(query))
          )
        })
          
      }
      
      // Mise à jour de la pagination
      this.filteredPersonnel = result 
      this.pagination.totalItems = result.length
      this.pagination.totalPages = Math.ceil(result.length / this.pagination.limit)
      // S'assurer que nous sommes à une page valide
      if (this.pagination.page > this.pagination.totalPages && this.pagination.totalPages > 0) {
        this.pagination.page = 1;
      }
    }
  }
})